<template>
  <div :class="$options.name" :data-e-ga-id="uniqueId">
    <div v-if="label || $slots.tooltip" class="tw:flex tw:flex-col tw:gap-1 tw:mb-1">
      <div class="tw:flex tw:gap-1 tw:items-center">
        <label v-if="label" class="tw:body-1">
          {{ label }}
        </label>
        <!--  @slot This slot is for the tooltip component.  -->
        <slot name="tooltip" />
      </div>
      <label v-if="description" class="tw:body-2 tw:text-default-700">
        {{ description }}
      </label>
    </div>
    <div
      v-bind="$attrs"
      :class="{
        EdroneNumberInput__inner: true,
        [`${$options.name}--error`]: invalid,
        [`${$options.name}--disabled`]: disabled,
      }"
    >
      <button
        :class="`${$options.name}__decrement`"
        :disabled="internalValue == min || disabled"
        :data-e-ga-id="uniqueId + '-decrement-button'"
        :data-testid="uniqueId + '-decrement-button'"
        @click="decrement"
      >
        -
      </button>
      <input
        v-model.number="internalValue"
        :class="`${$options.name}__input`"
        :disabled="disabled"
        :max="max"
        :min="min"
        type="number"
        @change="updateValueManually"
        @blur="handleBlur"
      />
      <button
        :class="`${$options.name}__increment`"
        :disabled="internalValue === max || disabled"
        :data-e-ga-id="uniqueId + '-increment-button'"
        :data-testid="uniqueId + '-increment-button'"
        @click="increment"
      >
        +
      </button>
    </div>
    <div v-if="helper || (invalid && errorMsg)" class="tw:mt-1">
      <p v-if="!disabled && invalid && errorMsg" class="tw:caption tw:text-red-500">
        {{ errorMsg }}
      </p>
      <p v-else-if="helper" class="tw:caption tw:!text-default-700">
        {{ helper }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneNumberInput",
  props: {
    /**
     * The label of the input.
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Description for label
     */
    description: {
      type: String,
      default: "",
      required: false,
    },
    /**
     * The helper text to display.
     */
    helper: {
      type: String,
      default: "",
    },
    /**
     * If true, the component will have a red border.
     */
    invalid: {
      type: Boolean,
      default: false,
    },
    /**
     * The error message to display.
     */
    errorMsg: {
      type: String,
      default: "",
    },
    /**
     * If true, the component will be disabled.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * The initial value of the input.
     * This component handles its own state, but if you want to control the input value as a prop, listen to the `updateValue` event and pass the new value here.
     * If you provide a value that is out of the `min` and `max` range, the input will set the value to the closest one.
     */
    value: {
      type: Number,
      default: undefined,
      required: false,
    },
    /**
     * The default value of the input.
     * Use this prop if you want to keep the input uncontrolled.
     * This prop is not reactive, so you can't change it after the component is mounted.
     * If the `value` prop is provided, this prop will be ignored.
     */
    defaultValue: {
      type: Number,
      default: undefined,
      required: false,
    },
    /**
     * The amount to increment or decrement the value when using the buttons.
     */
    step: {
      type: Number,
      default: 1,
    },
    /**
     * The minimum value of the input.
     */
    min: {
      type: Number,
      default: 0,
    },
    /**
     * The maximum value of the input.
     */
    max: {
      type: Number,
      default: 100,
    },
    uniqueId: {
      type: String,
      required: true,
    },
    /**
     *  If true, the value will be controlled.
     */
    controlled: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    /**
     * Emits the new value of the input.
     */
    updateValue: (value: number) => value,
    /**
     * Emits increment function.
     */
    increment: () => true,
    /**
     * Emits decrement function.
     */
    decrement: () => true,
    /**
     * Emits new input value provided manually.
     */
    updateValueManually: () => true,
  },
  setup(props, { emit }) {
    const internalValue = ref(props.value || props.defaultValue || props.min);
    function increment() {
      if (internalValue.value >= props.max) return;
      props.controlled
        ? (internalValue.value = (props.value || props.defaultValue || props.min) + props.step)
        : (internalValue.value = internalValue.value + props.step);
      emit("increment");
      emit("updateValue", internalValue.value);
      gtagUniversalSend({ component_id: props.uniqueId + "-increment-button" });
    }
    function decrement() {
      if (internalValue.value <= props.min) return;
      props.controlled
        ? (internalValue.value = (props.value || props.defaultValue || props.min) - props.step)
        : (internalValue.value = internalValue.value - props.step);
      emit("decrement");
      emit("updateValue", internalValue.value);
      gtagUniversalSend({ component_id: props.uniqueId + "-decrement-button" });
    }
    function updateValueManually() {
      emit("updateValueManually");
    }
    function handleBlur() {
      if (!internalValue.value) {
        internalValue.value = props.min;
      }
      if (internalValue.value < props.min) {
        internalValue.value = props.min;
      }

      if (internalValue.value > props.max) {
        internalValue.value = props.max;
      }

      if (props.controlled) {
        internalValue.value = props.value || props.defaultValue || props.min;
      }
    }

    watch(internalValue, (newValue) => {
      parseToInputValue(newValue);
    });

    watch(
      () => props.value,
      (newValue) => {
        if (newValue === internalValue.value) {
          return;
        }
        parseToInputValue(newValue);
      }
    );

    function parseToInputValue(value: unknown) {
      if (value === "") {
        return;
      }
      const inputValue = Number(value);
      props.controlled
        ? (internalValue.value = props.value || props.defaultValue || props.min)
        : (internalValue.value = inputValue);

      emit("updateValue", internalValue.value);
    }

    return {
      internalValue,
      increment,
      decrement,
      updateValueManually,
      handleBlur,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneNumberInput__inner {
  display: inline-flex;
}

.EdroneNumberInput * {
  transition: all 0.15s ease-out;
  box-sizing: border-box;
}

.EdroneNumberInput__input {
  min-width: 56px;
  height: 40px;
  border-left: none;
  border-right: none;
  text-align: center;
  width: 100%;
}

.EdroneNumberInput__input:focus-visible {
  outline: none;
}

/* hide number arrows while input is type="number" */
/* Chrome, Safari, Edge, Opera */
.EdroneNumberInput__input::-webkit-outer-spin-button,
.EdroneNumberInput__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
.EdroneNumberInput__input[type="number"] {
  -moz-appearance: textfield;
}

.EdroneNumberInput .EdroneNumberInput__input {
  @apply tw:border-y tw:border-default-200;
}

.EdroneNumberInput .EdroneNumberInput__decrement,
.EdroneNumberInput .EdroneNumberInput__increment {
  @apply tw:border tw:border-default-200;
}

.EdroneNumberInput .EdroneNumberInput__decrement:hover,
.EdroneNumberInput .EdroneNumberInput__increment:hover {
  /* hover on specific button */
  @apply tw:bg-default-200;
}

/* hover on whole component */
.EdroneNumberInput:hover .EdroneNumberInput__input {
  @apply tw:border-y tw:border-default-400;
}

.EdroneNumberInput:hover .EdroneNumberInput__decrement,
.EdroneNumberInput:hover .EdroneNumberInput__increment {
  @apply tw:border tw:border-default-400;
}

.EdroneNumberInput__decrement,
.EdroneNumberInput__increment {
  min-width: 40px;
  height: 40px;
  @apply tw:bg-default-100;
}

.EdroneNumberInput__decrement:focus,
.EdroneNumberInput__decrement:focus-within,
.EdroneNumberInput__increment:focus,
.EdroneNumberInput__increment:focus-within {
  outline: none;
  @apply tw:text-blue-500;
}

.EdroneNumberInput__decrement {
  border-right-width: 0 !important;
  border-radius: 3px 0 0 3px;
}

.EdroneNumberInput__increment {
  border-left-width: 0 !important;
  border-radius: 0 3px 3px 0;
}

.EdroneNumberInput--error .EdroneNumberInput__decrement,
.EdroneNumberInput--error .EdroneNumberInput__increment,
.EdroneNumberInput--error:hover .EdroneNumberInput__decrement,
.EdroneNumberInput--error:hover .EdroneNumberInput__increment {
  @apply tw:border tw:border-red-500;
}

.EdroneNumberInput--error .EdroneNumberInput__decrement:hover,
.EdroneNumberInput--error .EdroneNumberInput__increment:hover,
.EdroneNumberInput--error:hover .EdroneNumberInput__decrement:hover,
.EdroneNumberInput--error:hover .EdroneNumberInput__increment:hover {
  /* hover on specific button */
  @apply tw:bg-default-200;
}

.EdroneNumberInput--error .EdroneNumberInput__input,
.EdroneNumberInput--error:hover .EdroneNumberInput__input {
  @apply tw:border-y tw:border-red-500;
}

.EdroneNumberInput--disabled .EdroneNumberInput__decrement,
.EdroneNumberInput--disabled .EdroneNumberInput__increment {
  border: none !important;
  @apply tw:!bg-default-100;
}

.EdroneNumberInput--disabled .EdroneNumberInput__decrement:hover,
.EdroneNumberInput--disabled .EdroneNumberInput__increment:hover {
  /* hover on specific button */
  @apply tw:!bg-default-100;
}

.EdroneNumberInput--disabled .EdroneNumberInput__input {
  border-top: none !important;
  border-bottom: none !important;
  @apply tw:text-default-500;
}
</style>
